'use client';

import { GoogleAnalytics } from '@next/third-parties/google';
import { useCookieConsentStore } from '@/stores/cookie-consent.store';
import { useThemeStore } from '@/stores/theme.store';

/**
 * Mounts one `<GoogleAnalytics>` per resolved tracker ID. Renders nothing
 * until analytics consent is granted, so no GA scripts load before the
 * user accepts.
 *
 * Page-view tracking is delegated to GA4 Enhanced Measurement (default-on
 * in the GA admin panel), which the `<GoogleAnalytics>` component already
 * wires up via browser-history events. Manually calling `trackPageView`
 * here would double-fire `page_view` and inflate session/pageview counts.
 * See https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag#manual_pageviews
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const analyticsConsent = useCookieConsentStore((state) => state.preferences.analytics);
  const theme = useThemeStore((state) => state.theme);

  // Build the tracker ID list: theme.trackerIds (per-tenant) + env fallback
  const themeTrackers = theme?.trackerIds ?? [];
  const envTracker = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKER_ID;
  const trackerIds = [
    ...themeTrackers,
    ...(envTracker && !themeTrackers.includes(envTracker) ? [envTracker] : []),
  ];

  return (
    <>
      {children}
      {analyticsConsent && trackerIds.map((gaId) => <GoogleAnalytics key={gaId} gaId={gaId} />)}
    </>
  );
}
